/**
 * WishesWall — wishes & greetings saved to a member's life timeline.
 * Per-wish extras: group-card signing, voice playback, scheduled badge,
 * one-tap translation (EN/HI/ML/TA) and AI greeting-card generation.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Trash2, PenLine, Loader2, Palette, Download } from "lucide-react";

const TONE_BG = { remembrances: "#F5F3FF", health_support: "#FDF2F8" };
const LANG_LABELS = { en: "English", hi: "हिन्दी", ml: "മലയാളം", ta: "தமிழ்" };

function SignRow({ treeId, wishId, onSigned }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const sign = async () => {
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/wishes/${wishId}/sign`, { message: note.trim() || undefined });
      toast.success("You've signed the card!");
      setNote("");
      onSigned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't sign.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5 mt-1.5" data-testid={`wish-sign-row-${wishId}`}>
      <input
        className="wofy-input !py-1.5 !text-xs flex-1"
        maxLength={500}
        placeholder="Add your note (optional) and sign…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid={`wish-sign-input-${wishId}`}
      />
      <button type="button" disabled={busy} onClick={sign} className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-white inline-flex items-center gap-1" style={{ background: "#B25D38" }} data-testid={`wish-sign-btn-${wishId}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PenLine className="w-3 h-3" />} Sign
      </button>
    </div>
  );
}

const CARD_STYLE_OPTIONS = [
  { key: "kerala", label: "🌺 Kerala Floral" },
  { key: "rajasthani", label: "👑 Royal Rajasthani" },
  { key: "minimal", label: "✨ Modern Minimal" },
  { key: "classic", label: "🪔 Classic Festive" },
];

function WishCard({ w, treeId, meId, canModerate, onChanged, tributesEnabled }) {
  const [card, setCard] = useState({ status: w.card_status, url: w.card_url });
  const [cardBusy, setCardBusy] = useState(w.card_status === "generating");
  const [stylePick, setStylePick] = useState(false);
  const [trans, setTrans] = useState(null); // { lang, text }
  const [transBusy, setTransBusy] = useState(null);
  const tu = w.tribute_users || {};
  const [tributes, setTributes] = useState({
    diya: (tu.diya || []).length,
    flower: (tu.flower || []).length,
    mine: { diya: (tu.diya || []).includes(meId), flower: (tu.flower || []).includes(meId) },
  });
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const giveTribute = async (kind) => {
    try {
      const { data } = await http.post(`/family-trees/${treeId}/wishes/${w.id}/tribute`, { kind });
      setTributes((t) => ({ ...t, ...data.counts, mine: { ...t.mine, [kind]: data.mine } }));
      if (data.mine) toast.success(kind === "diya" ? "🪔 You lit a diya in their memory." : "🌸 You laid a flower.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add your tribute.");
    }
  };

  const startCard = async (style) => {
    setStylePick(false);
    setCardBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/wishes/${w.id}/card`, { style });
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await http.get(`/family-trees/${treeId}/wishes/${w.id}`);
          if (data.card_status === "done" && data.card_url) {
            clearInterval(pollRef.current);
            setCard({ status: "done", url: data.card_url });
            setCardBusy(false);
            toast.success("Your greeting card is ready!");
          } else if (data.card_status === "error") {
            clearInterval(pollRef.current);
            setCardBusy(false);
            toast.error(data.card_error || "Card generation failed — try again.");
          }
        } catch {}
      }, 4000);
    } catch (e) {
      setCardBusy(false);
      toast.error(e?.response?.data?.detail || "Couldn't start the card.");
    }
  };

  const translate = async (lang) => {
    setTransBusy(lang);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/wishes/${w.id}/translate`, { lang });
      setTrans(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed.");
    } finally {
      setTransBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this wish?")) return;
    try {
      await http.delete(`/family-trees/${treeId}/wishes/${w.id}`);
      toast.success("Removed");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <div className="rounded-xl border p-3 relative" style={{ borderColor: "var(--wofy-border)", background: TONE_BG[w.category_key] || "#FFFDF7" }} data-testid={`wish-item-${w.id}`}>
      <div className="flex flex-wrap items-baseline gap-2 pr-6">
        <span className="text-[10px] font-mono uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "#FFF7ED", color: "#B25D38" }}>{w.occasion_label}</span>
        {w.visibility === "private" && <span className="text-[10px] opacity-50">🔒 private</span>}
        {w.scheduled && <span className="text-[10px] rounded-full px-2 py-0.5" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`wish-scheduled-${w.id}`}>⏰ scheduled for {w.deliver_on}</span>}
        {w.is_group_card && <span className="text-[10px] rounded-full px-2 py-0.5" style={{ background: "#EDE9FE", color: "#5B4A73" }}>✍️ group card</span>}
      </div>

      {w.message && (
        <p className="text-sm mt-1.5 whitespace-pre-wrap" data-testid={`wish-text-${w.id}`}>
          {trans ? trans.text : w.message}
        </p>
      )}
      {trans && <div className="text-[10px] opacity-50 mt-0.5">Translated to {LANG_LABELS[trans.lang]}</div>}

      {meId && w.message && (
        <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={`wish-translate-row-${w.id}`}>
          {trans && (
            <button type="button" onClick={() => setTrans(null)} className="text-[10px] rounded-full border px-2 py-0.5 font-semibold" style={{ borderColor: "#B25D38", color: "#B25D38" }} data-testid={`wish-translate-original-${w.id}`}>
              Original
            </button>
          )}
          {Object.entries(LANG_LABELS).map(([lang, label]) => (
            (!trans || trans.lang !== lang) && (
              <button
                key={lang}
                type="button"
                disabled={transBusy === lang}
                onClick={() => translate(lang)}
                className="text-[10px] rounded-full border px-2 py-0.5 opacity-70 hover:opacity-100"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`wish-translate-${lang}-${w.id}`}
              >
                {transBusy === lang ? "…" : label}
              </button>
            )
          ))}
        </div>
      )}

      {w.audio_url && (
        <audio controls preload="none" src={resolveImage(w.audio_url)} className="w-full mt-2 h-9" data-testid={`wish-audio-${w.id}`} />
      )}

      {/* AI greeting card */}
      {card.status === "done" && card.url ? (
        <div className="mt-2" data-testid={`wish-card-image-${w.id}`}>
          <img src={resolveImage(card.url)} alt="Greeting card" className="rounded-xl border max-h-72 object-contain" style={{ borderColor: "var(--wofy-border)" }} loading="lazy" />
          <div className="flex gap-2 mt-1.5">
            <a href={resolveImage(card.url)} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border px-2.5 py-1" style={{ borderColor: "var(--wofy-border)" }} data-testid={`wish-card-download-${w.id}`}>
              <Download className="w-3 h-3" /> Download
            </a>
            <button type="button" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`🎉 ${w.occasion_label} card for ${w.member_name}: ${resolveImage(card.url)}`)}`, "_blank", "noopener")} className="text-[11px] font-semibold rounded-full px-2.5 py-1 text-white" style={{ background: "#25D366" }} data-testid={`wish-card-share-${w.id}`}>
              Share on WhatsApp
            </button>
          </div>
        </div>
      ) : meId ? (
        <div className="mt-2">
          {!stylePick ? (
            <button
              type="button"
              disabled={cardBusy}
              onClick={() => setStylePick(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full border px-2.5 py-1"
              style={{ borderColor: "var(--wofy-border)", color: "#B25D38" }}
              data-testid={`wish-card-btn-${w.id}`}
            >
              {cardBusy ? (<><Loader2 className="w-3 h-3 animate-spin" /> Designing your card… ~20s</>) : (<><Palette className="w-3 h-3" /> Create AI greeting card</>)}
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5" data-testid={`wish-card-styles-${w.id}`}>
              <span className="text-[10px] opacity-60">Pick a style:</span>
              {CARD_STYLE_OPTIONS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => startCard(s.key)}
                  className="text-[10px] font-semibold rounded-full border px-2 py-0.5 hover:shadow-sm"
                  style={{ borderColor: "var(--wofy-border)", background: "#FFF7ED", color: "#B25D38" }}
                  data-testid={`wish-card-style-${s.key}-${w.id}`}
                >
                  {s.label}
                </button>
              ))}
              <button type="button" onClick={() => setStylePick(false)} className="text-[10px] opacity-50 hover:opacity-100" data-testid={`wish-card-style-cancel-${w.id}`}>Cancel</button>
            </div>
          )}
        </div>
      ) : null}

      {w.category_key === "remembrances" && tributesEnabled && meId && (
        <div className="flex items-center gap-2 mt-2" data-testid={`wish-tributes-${w.id}`}>
          <button
            type="button"
            onClick={() => giveTribute("diya")}
            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition"
            style={tributes.mine.diya ? { borderColor: "#B45309", background: "#FEF3C7", color: "#92400E" } : { borderColor: "var(--wofy-border)" }}
            data-testid={`wish-tribute-diya-${w.id}`}
          >
            🪔 {tributes.mine.diya ? "Diya lit" : "Light a diya"}{tributes.diya ? ` · ${tributes.diya}` : ""}
          </button>
          <button
            type="button"
            onClick={() => giveTribute("flower")}
            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition"
            style={tributes.mine.flower ? { borderColor: "#BE185D", background: "#FDF2F8", color: "#9D174D" } : { borderColor: "var(--wofy-border)" }}
            data-testid={`wish-tribute-flower-${w.id}`}
          >
            🌸 {tributes.mine.flower ? "Flower laid" : "Lay a flower"}{tributes.flower ? ` · ${tributes.flower}` : ""}
          </button>
        </div>
      )}

      <div className="text-[11px] opacity-60 mt-1.5">— {w.sender_name}{w.created_at ? ` · ${w.created_at.slice(0, 10)}` : ""}</div>

      {w.is_group_card && (
        <div className="mt-2 border-t pt-2" style={{ borderColor: "var(--wofy-border)" }} data-testid={`wish-signatures-${w.id}`}>
          <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
            {(w.signatures || []).length} signature{(w.signatures || []).length === 1 ? "" : "s"}
          </div>
          {(w.signatures || []).map((s, i) => (
            <div key={i} className="text-xs mt-0.5">
              <b>{s.name}</b>{s.message ? <span className="opacity-80"> — “{s.message}”</span> : null}
            </div>
          ))}
          {meId && <SignRow treeId={treeId} wishId={w.id} onSigned={onChanged} />}
        </div>
      )}

      {(canModerate || w.sender_id === meId) && (
        <button type="button" onClick={remove} className="absolute top-2 right-2 opacity-40 hover:opacity-100" data-testid={`wish-delete-${w.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default function WishesWall({ treeId, memberId, canModerate, meId }) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ tributes_enabled: true, can_admin: false });

  const load = useCallback(() => {
    if (!treeId || !memberId) return;
    http.get(`/family-trees/${treeId}/wishes`, { params: { member_id: memberId } })
      .then((r) => {
        setItems(r.data.items || []);
        setMeta({ tributes_enabled: r.data.tributes_enabled !== false, can_admin: !!r.data.can_admin });
      })
      .catch(() => {});
  }, [treeId, memberId]);
  useEffect(load, [load]);

  const toggleTributes = async () => {
    try {
      const next = !meta.tributes_enabled;
      await http.patch(`/family-trees/${treeId}/tributes-setting`, { enabled: next });
      setMeta((m) => ({ ...m, tributes_enabled: next }));
      toast.success(next ? "Candles & flowers are on." : "Candles & flowers turned off for this family.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update.");
    }
  };

  if (!items.length) return null;
  return (
    <section className="wofy-card !p-5 mb-4" data-testid="member-wishes-wall">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Gift className="w-4 h-4" style={{ color: "#B25D38" }} />
          <h2 className="font-display text-xl">Wishes & Greetings ({items.length})</h2>
        </div>
        {meta.can_admin && (
          <label className="flex items-center gap-1.5 text-[11px] opacity-80 cursor-pointer select-none" data-testid="wishes-tributes-toggle-label">
            <input type="checkbox" checked={meta.tributes_enabled} onChange={toggleTributes} data-testid="wishes-tributes-toggle" />
            🪔 Candles &amp; flowers
          </label>
        )}
      </div>
      <div className="space-y-3">
        {items.map((w) => (
          <WishCard key={w.id} w={w} treeId={treeId} meId={meId} canModerate={canModerate} onChanged={load} tributesEnabled={meta.tributes_enabled} />
        ))}
      </div>
    </section>
  );
}
